/**
 * Custom hook for request filtering logic
 * Auto-applies filters with debouncing for text input
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { matchesDateFilter } from '../../../components/common/Reviews_Cards/tabUtils';
import { useRequestTags } from './useRequestTags';

const TEXT_DEBOUNCE_MS = 500;

export const useRequestFilters = (requests = [], status = null) => {
  const { getRequestTag } = useRequestTags();

  // Single source of truth for filters
  const [filters, setFilters] = useState({
    referenceOrStudyName: '',
    dateRange: null,
  });

  // Text input debouncing
  const [debouncedTextFilter, setDebouncedTextFilter] = useState('');
  const textDebounceRef = useRef();

  const [selectedTags, setSelectedTags] = useState([]);

  // Debounce text input changes
  useEffect(() => {
    clearTimeout(textDebounceRef.current);
    textDebounceRef.current = setTimeout(() => {
      setDebouncedTextFilter(filters.referenceOrStudyName);
    }, TEXT_DEBOUNCE_MS);

    return () => clearTimeout(textDebounceRef.current);
  }, [filters.referenceOrStudyName]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleTagChange = useCallback((newSelectedTags) => {
    setSelectedTags(newSelectedTags);
  }, []);

  // Auto-apply filtering using the debounced text and immediate date/tag changes
  const filteredRequests = useMemo(() => {
    if (!requests?.length) return [];

    return requests.filter((request) => {
      // Text search (debounced)
      const searchTerm = debouncedTextFilter.toLowerCase();
      const textMatch =
        !searchTerm ||
        request.generated_id?.toLowerCase().includes(searchTerm) ||
        request.study_name?.toLowerCase().includes(searchTerm);

      // Date range (immediate)
      const dateMatch = matchesDateFilter(request, filters.dateRange);

      // Tag filter (immediate)
      let tagMatch = true;
      if (selectedTags.length > 0) {
        const requestTag = getRequestTag(request, status);
        tagMatch = requestTag && selectedTags.includes(requestTag);
      }

      return textMatch && dateMatch && tagMatch;
    });
  }, [
    requests,
    debouncedTextFilter,
    filters.dateRange,
    selectedTags,
    status,
    getRequestTag,
  ]);

  return {
    filters,
    selectedTags,
    handleFilterChange,
    handleTagChange,
    filteredRequests,
  };
};
